import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Button, Flex, Input, Popconfirm, Space, Spin, Table, Tooltip, Typography } from 'antd';
import { DeleteFilled, EditFilled, QuestionCircleFilled, SearchOutlined, SyncOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Components
import BooleanIcon from '../../utils/BooleanIcon';
import ImageRender from '../../utils/ImageRender';

// Actions
import { get_all_items, set_item_create_id, item_delete } from '../../../redux/actions/itemsActions';

// Utils
import { get_item_type_desc, get_item_usage_desc } from '../../../utils/get_status_desc';

const ItemsTable = (props) => {
	const { messageApi, setIsModalOpen, setUpdate, setItemToUpdate, pageSize, setPageSize, skip, setSkip } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const { all_items_loading, all_items, item_delete_loading } = useSelector(state => state.items);

	const searchInput = useRef(null);
	const [searchText, setSearchText] = useState('');

	const handleSearch = (selectedKeys) => {
		const text = selectedKeys[0];
		setSearchText(text);
		
		dispatch(get_all_items({ name: text }));
	};

	const handleReset = (clearFilters) => {
		clearFilters();
		setSearchText('');

		const itemsFilters = { skip: skip, limit: pageSize };
		dispatch(get_all_items(itemsFilters));
	};

	const getColumnSearchProps = (dataIndex) => ({
		filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
			<div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
				<Input
					style={{ marginBottom: 8, display: 'block' }}
					ref={searchInput}
					placeholder={t('form.placeholders.searchItem')}
					value={selectedKeys[0]}
					onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
					onPressEnter={() => {
						handleSearch(selectedKeys);
						close();
					}}
				/>
				<Space>
					<Button
						style={{ width: 95 }}
						type='primary'
						size='small'
						icon={<SearchOutlined />}
						onClick={() => {
							handleSearch(selectedKeys);
							close();
						}}
					>
						{t('buttons.search')}
					</Button>
					<Button
						style={{ width: 110 }}
						size='small'
						icon={<SyncOutlined />}
						onClick={() => {
							clearFilters && handleReset(clearFilters);
							close();
						}}
					>
						{t('buttons.reset')}
					</Button>
				</Space>
			</div>
		),
		filterIcon: () => (
			<SearchOutlined
				style={{
					color: searchText.length > 0 ? '#F18B71' : undefined
				}}
			/>
		),
		onFilter: (value, record) =>
			record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
		onFilterDropdownOpenChange: (visible) => {
			if (visible) {
				setTimeout(() => searchInput.current?.select(), 100);
			}
		},
		render: (text) => text
	});

	const update = (record) => {
		setIsModalOpen(true);
		setUpdate(true);
		dispatch(set_item_create_id(record._id.$oid));
		setItemToUpdate(record);
	}

	const deleteItem = (record) => {
		let newSkip = skip;

		if ((all_items.count - 1) % pageSize === 0) {
			newSkip -= pageSize;
			setSkip(newSkip);
			localStorage.setItem('itemsSkip', newSkip);
		}

		const filters = { skip: newSkip, limit: pageSize };
		dispatch(item_delete(messageApi, record._id.$oid, filters));
	}

	const columns = [
		{
			key: 'image',
			title: t('form.label.image'),
			dataIndex: 'image',
			render: (_, record) => (
				<Flex justify='center' style={{ width: '50%' }}>
					<ImageRender
						key={record.asset}
						url={`${process.env.REACT_APP_SERVER_URL}/api/recipes/items/${record._id.$oid}/asset`}
						size={{ width: 50, height: 50 }}
					/>
				</Flex>
			)
		},
		{
			key: 'name',
			title: t('form.label.name'),
			dataIndex: 'name',
			...getColumnSearchProps('name'),
			render: (_, record) => (
				<Typography.Text copyable={{ text: record._id.$oid, tooltips: [t('table.copyableText.copyId'), t('table.copyableText.copiedId')] }}>
					{record.name}
				</Typography.Text>
			)
		},
		{
			key: 'description',
			title: t('form.label.description'),
			dataIndex: 'description'
		},
		{
			key: 'scale',
			title: t('form.label.scale'),
			dataIndex: 'scale',
			render: (_, record) => (
				<Typography.Text>
					{record.scale !== undefined && record.scale !== null ? record.scale : '-'}
				</Typography.Text>
			)
		},
		{
			key: 'item_type',
			title: t('form.label.itemType'),
			dataIndex: 'item_type',
			render: (_, record) => (
				<Typography.Text>
					{record.item_type ? get_item_type_desc(record.item_type) : '-'}
				</Typography.Text>
			)
		},
		{
			key: 'content_scale',
			title: t('form.label.itemContentScale'),
			dataIndex: 'content_scale',
			render: (_, record) => (
				<Typography.Text>
					{record.content_scale !== undefined && record.content_scale !== null ? record.content_scale : '-'}
				</Typography.Text>
			)
		},
		{
			key: 'usage',
			title: t('form.label.itemUsage'),
			dataIndex: 'usage',
			render: (_, record) => (
				<Typography.Text>
					{record.usage !== undefined && record.usage !== null ? get_item_usage_desc(record.usage) : '-'}
				</Typography.Text>
			)
		},
		{
			key: 'particles',
			title: t('form.label.particles'),
			dataIndex: 'particles',
			render: (particles) => <BooleanIcon value={particles} />
		},
		{
			key: 'action',
			title: t('table.headers.actions'),
			dataIndex: 'action',
			render: (_, record) => (
				<Space>
					<Tooltip title={t('buttons.edit')}>
						<Button
							type='primary'
							style={{ backgroundColor: '#80B4B9' }}
							shape='circle'
							size='large'
							icon={<EditFilled />}
							onClick={() => update(record)}
						/>
					</Tooltip>
					<Popconfirm
						title={t('items.delete.confirmTitle')}
						description={t('items.delete.confirmText')}
						icon={<QuestionCircleFilled style={{ color: '#1677FF' }} />}
						onConfirm={() => deleteItem(record)}
						okButtonProps={{
							loading: item_delete_loading
						}}
						okText={t('buttons.confirm')}
						cancelText={t('buttons.cancel')}
					>
						<Button
							type='primary'
							shape='circle'
							size='large'
							danger
							icon={<DeleteFilled />}
						/>
					</Popconfirm>
				</Space>
			)
		}
	];

	const handleTableChange = (pagination, filters, sorter) => {
		const skip = (pagination.current - 1) * pagination.pageSize;
		setSkip(skip);
		localStorage.setItem('itemsSkip', skip);

		const limit = pagination.pageSize;
		setPageSize(limit);
		localStorage.setItem('itemsPageSize', limit);

		const itemsFilters = { skip, limit };
		dispatch(get_all_items(itemsFilters));
	};

	return (
		<Spin spinning={all_items_loading}>
			<Table
				columns={columns}
				dataSource={all_items.items}
				onChange={handleTableChange}
				scroll={{
					x: 'auto'
				}}
				pagination={{
					pageSize: pageSize, 
					total: all_items.count,
					current: (skip / pageSize) + 1,
					showSizeChanger: true,
					showTotal: (total, range) => `${range[0]} - ${range[1]} ${t('table.pagination.of')} ${total}`,
					showTitle: false,
					responsive: true,
					locale: { items_per_page: `/ ${t('table.pagination.page')}` }
				}}
			/>
		</Spin>
	)
}

ItemsTable.propTypes = {
	messageApi: PropTypes.object.isRequired,
	setIsModalOpen: PropTypes.func.isRequired,
	setUpdate: PropTypes.func.isRequired,
	setItemToUpdate: PropTypes.func.isRequired,
	pageSize: PropTypes.number.isRequired,
	setPageSize: PropTypes.func.isRequired,
	skip: PropTypes.number.isRequired,
	setSkip: PropTypes.func.isRequired
}

export default ItemsTable;
